import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '../services/supabaseService';

/**
 * Generuje URL-friendly slug z polskiego tytułu
 * "Eklektyczne wnętrza dla wymagającego panicza" → "eklektyczne-wnetrza-dla-wymagajacego-panicza"
 */
const toSlug = (text) => {
  if (!text) return '';
  const polishMap = {
    'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n',
    'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z',
    'Ą': 'A', 'Ć': 'C', 'Ę': 'E', 'Ł': 'L', 'Ń': 'N',
    'Ó': 'O', 'Ś': 'S', 'Ź': 'Z', 'Ż': 'Z'
  };
  return text
    .replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, (ch) => polishMap[ch] || ch)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
};

/**
 * Buduje pełny URL ogłoszenia
 * /m/{id}-{slug}  →  /m/22-eklektyczne-wnetrza
 * /n/{id}-{slug}  →  /n/5-jan-kowalski
 */
export const buildPropertyUrl = (property) => {
  if (!property?.id) return '/';
  const slug = toSlug(property.title);
  return slug ? `/m/${property.id}-${slug}` : `/m/${property.id}`;
};

export const buildTenantUrl = (tenant) => {
  if (!tenant?.id) return '/';
  const slug = toSlug(tenant.first_name || tenant.name || '');
  return slug ? `/n/${tenant.id}-${slug}` : `/n/${tenant.id}`;
};

/**
 * Parsuje URL i zwraca { type, id } lub null
 * /m/22-eklektyczne-wnetrza  →  { type: 'property', id: 22 }
 * /n/5-jan-kowalski           →  { type: 'tenant', id: 5 }
 */
const parseUrl = (pathname) => {
  const propertyMatch = pathname.match(/^\/m\/(\d+)/);
  if (propertyMatch) return { type: 'property', id: parseInt(propertyMatch[1], 10) };

  const tenantMatch = pathname.match(/^\/n\/(\d+)/);
  if (tenantMatch) return { type: 'tenant', id: parseInt(tenantMatch[1], 10) };

  return null;
};

/**
 * Hook do zarządzania sharowalnymi URL-ami.
 * 
 * - Przy otwarciu modala → pushState zmienia URL
 * - Przy zamknięciu → pushState wraca do /
 * - Przy wejściu na /m/22 → automatycznie otwiera ogłoszenie
 * - Przycisk "wstecz" → zamyka modal
 * 
 * @param {Function} onOpenProperty  - (property) => void
 * @param {Function} onOpenTenant    - (tenant) => void
 * @param {Function} onClose         - () => void — wywoływane przy popstate (wstecz)
 */
export const useShareableUrl = ({ onOpenProperty, onOpenTenant, onClose }) => {
  const isNavigatingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);

  // Przy zamontowaniu: sprawdź URL i otwórz odpowiedni modal
  useEffect(() => {
    if (initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;

    const parsed = parseUrl(window.location.pathname);
    if (!parsed) return;

    const loadFromUrl = async () => {
      try {
        if (parsed.type === 'property') {
          const { data, error } = await supabase
            .from('properties')
            .select('*')
            .eq('id', parsed.id)
            .single();
          if (!error && data && onOpenProperty) {
            // Ustaw tytuł strony dla Googlebota i przeglądarki
            if (data.title) {
              const city = data.city ? ` — ${data.city}` : '';
              document.title = `${data.title}${city} | dobrynajemca.pl`;
            }
            // Dodaj / do historii, żeby przycisk "wstecz" zamykał modal zamiast opuszczać stronę
            const currentPath = window.location.pathname;
            window.history.replaceState({}, '', '/');
            window.history.pushState({ type: 'property', id: parsed.id }, '', currentPath);
            isNavigatingRef.current = true;
            onOpenProperty(data);
            isNavigatingRef.current = false;
          }
        } else if (parsed.type === 'tenant') {
          const { data, error } = await supabase
            .from('tenant_profiles')
            .select('*')
            .eq('id', parsed.id)
            .single();
          if (!error && data && onOpenTenant) {
            // Ustaw tytuł strony dla Googlebota i przeglądarki
            const name = data.first_name || data.name || 'Profil najemcy';
            document.title = `${name} — profil najemcy | dobrynajemca.pl`;
            const currentPath = window.location.pathname;
            window.history.replaceState({}, '', '/');
            window.history.pushState({ type: 'tenant', id: parsed.id }, '', currentPath);
            isNavigatingRef.current = true;
            onOpenTenant(data);
            isNavigatingRef.current = false;
          }
        }
      } catch (e) {
        // Nieprawidłowy URL — ignoruj
      }
    };

    loadFromUrl();
  }, [onOpenProperty, onOpenTenant]);

  // Obsługa przycisku "wstecz"
  useEffect(() => {
    const handlePopState = () => {
      const parsed = parseUrl(window.location.pathname);
      if (!parsed && onClose) {
        onClose();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [onClose]);

  // Zmień URL przy otwarciu modala
  const pushPropertyUrl = useCallback((property) => {
    if (isNavigatingRef.current) return; // Nie pushuj jeśli ładujemy z URL
    const url = buildPropertyUrl(property);
    if (window.location.pathname !== url) {
      window.history.pushState({ type: 'property', id: property.id }, '', url);
    }
  }, []);

  const pushTenantUrl = useCallback((tenant) => {
    if (isNavigatingRef.current) return;
    const url = buildTenantUrl(tenant);
    if (window.location.pathname !== url) {
      window.history.pushState({ type: 'tenant', id: tenant.id }, '', url);
    }
  }, []);

  // Przywróć / przy zamknięciu modala
  const resetUrl = useCallback(() => {
    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/');
    }
  }, []);

  // Kopiuj link do schowka (z native share na mobile)
  const shareUrl = useCallback(async (property) => {
    const url = `${window.location.origin}${buildPropertyUrl(property)}`;
    const shareData = {
      title: property.title || 'Ogłoszenie',
      text: property.title || 'Zobacz ogłoszenie',
      url
    };

    // Na mobile użyj natywnego share (iOS/Android)
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return { success: true, method: 'native' };
      } catch (e) {
        if (e.name === 'AbortError') return { success: false, method: 'cancelled' };
        // Fallback do clipboard
      }
    }

    // Desktop/fallback — kopiuj do schowka
    try {
      await navigator.clipboard.writeText(url);
      return { success: true, method: 'clipboard' };
    } catch {
      return { success: false, method: 'none' };
    }
  }, []);

  const shareTenantUrl = useCallback(async (tenant) => {
    const url = `${window.location.origin}${buildTenantUrl(tenant)}`;
    const shareData = {
      title: tenant.first_name || tenant.name || 'Profil najemcy',
      text: `Profil najemcy: ${tenant.first_name || tenant.name || ''}`,
      url
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return { success: true, method: 'native' };
      } catch (e) {
        if (e.name === 'AbortError') return { success: false, method: 'cancelled' };
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      return { success: true, method: 'clipboard' };
    } catch {
      return { success: false, method: 'none' };
    }
  }, []);

  return {
    pushPropertyUrl,
    pushTenantUrl,
    resetUrl,
    shareUrl,
    shareTenantUrl
  };
};
